import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginDto } from '@triade/contracts';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { getEscopo, getToken, iniciarSessao } from '@/lib/session';
import { useLogin } from '@/api/auth';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (getToken()) throw redirect({ to: getEscopo() === 'TROCA_SENHA' ? '/trocar-senha' : '/' });
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginDto>({ resolver: zodResolver(loginSchema), defaultValues: { email: '', password: '' } });

  const onSubmit = handleSubmit(async (dados) => {
    try {
      const resp = await login.mutateAsync(dados);
      iniciarSessao(resp.accessToken, resp.escopo);
      await navigate({ to: resp.deveTrocarSenha ? '/trocar-senha' : '/' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && err.code === 'SENHA_TEMPORARIA_EXPIRADA') {
        setError('root', { message: err.message });
      } else if (err instanceof ApiError && err.status === 401) {
        setError('root', { message: 'E-mail ou senha inválidos.' });
      } else {
        setError('root', { message: 'Não foi possível entrar. Tente de novo.' });
      }
    }
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <section
        aria-labelledby="titulo-login"
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-float"
      >
        <div className="mb-6 flex items-center gap-3">
          <div aria-hidden="true" className="flex size-8 items-center justify-center rounded-xs bg-primary text-title font-bold text-primary-foreground">
            X
          </div>
          <div>
            <div className="text-body font-semibold tracking-[0.08em]">XARP</div>
            <div className="text-caption text-muted-foreground">Precificação</div>
          </div>
        </div>
        <h1 id="titulo-login" className="text-title font-semibold">
          Entrar
        </h1>
        <p className="mt-1 text-small text-ink-secondary">Use o e-mail e a senha do seu usuário.</p>

        <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" autoComplete="username" aria-invalid={!!errors.email} {...register('email')} />
            {errors.email && <p className="text-caption text-danger-ink">{errors.email.message}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            {errors.password && <p className="text-caption text-danger-ink">{errors.password.message}</p>}
          </div>

          {errors.root && (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
              {errors.root.message}
            </p>
          )}

          <Button type="submit" disabled={login.isPending} className="h-10 font-semibold">
            {login.isPending ? 'Entrando…' : 'Entrar'}
          </Button>
        </form>
      </section>
    </main>
  );
}
